import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Flame, Zap, BookOpen, Award, RotateCcw, Trash2, AlertTriangle, Loader2, Mail } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { BADGES } from "@/lib/badges";

export default function Profile() {
  const { user, setUser, logout } = useAuth();
  const navigate = useNavigate();
  const [progress, setProgress] = useState(null);

  const [editName, setEditName] = useState("");
  const [editEmail, setEditEmail] = useState("");
  const [editPassword, setEditPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const [resettingData, setResettingData] = useState(false);
  const [deletePassword, setDeletePassword] = useState("");
  const [deletingAccount, setDeletingAccount] = useState(false);
  const [savingNotifications, setSavingNotifications] = useState(false);

  useEffect(() => {
    if (user) {
      setEditName(user.name || "");
      setEditEmail(user.email || "");
    }
  }, [user]);

  useEffect(() => {
    api.get("/progress").then((r) => setProgress(r.data)).catch(() => {});
  }, []);

  const p = progress || { xp: 0, streak: 0, completed_count: 0, total_lessons: 0, per_course: {} };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (editPassword && editPassword !== confirmPassword) {
      toast.error("Passwoerter stimmen nicht ueberein!");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        name: editName,
        email: editEmail,
      };
      if (editPassword) {
        payload.password = editPassword;
      }
      const { data } = await api.post("/auth/update", payload);
      setUser(data.user);
      toast.success("Zugangsdaten erfolgreich aktualisiert! 👋");
      setEditPassword("");
      setConfirmPassword("");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Fehler beim Aktualisieren");
    } finally {
      setLoading(false);
    }
  };

  const handleToggleBadgeEmails = async (checked) => {
    setSavingNotifications(true);
    try {
      const { data } = await api.post("/auth/notifications", { notify_badges_email: checked });
      setUser(data);
      toast.success(checked ? "E-Mails bei neuen Abzeichen aktiviert" : "E-Mails bei neuen Abzeichen deaktiviert");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Fehler beim Speichern");
    } finally {
      setSavingNotifications(false);
    }
  };

  const handleResetData = async () => {
    if (!confirm("Sollen wirklich ALLE deine Daten zurückgesetzt werden? XP, Streak, abgeschlossene Lektionen, Quiz-Ergebnisse, gelöste Challenges, die Tages-Challenge-Serie und alle Flashcard-Fortschritte gehen unwiderruflich verloren. Dein Konto selbst bleibt erhalten.")) {
      return;
    }
    setResettingData(true);
    try {
      const { data } = await api.post("/auth/reset-data");
      setUser(data.user);
      const { data: freshProgress } = await api.get("/progress");
      setProgress(freshProgress);
      localStorage.removeItem(`cq_badges_seen_${data.user.id}`);
      toast.success("Alle Daten wurden zurückgesetzt.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Fehler beim Zurücksetzen");
    } finally {
      setResettingData(false);
    }
  };

  const handleDeleteAccount = async (e) => {
    e.preventDefault();
    if (!deletePassword) {
      toast.error("Bitte gib dein Passwort ein, um das Konto zu löschen.");
      return;
    }
    if (!confirm("Soll dein Konto WIRKLICH endgültig gelöscht werden? Das kann nicht rückgängig gemacht werden.")) {
      return;
    }
    setDeletingAccount(true);
    try {
      await api.delete("/auth/me", { data: { password: deletePassword } });
      toast.success("Konto wurde gelöscht.");
      logout();
      navigate("/");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Fehler beim Löschen des Kontos");
    } finally {
      setDeletingAccount(false);
      setDeletePassword("");
    }
  };

  return (
    <div className="space-y-8" data-testid="profile-page">
      <div className="nb-card p-6 sm:p-8 flex items-center gap-5 animate-fade-up">
        <div className="h-20 w-20 bg-secondary border-2 border-black rounded-2xl flex items-center justify-center text-white font-heading font-black text-3xl nb-shadow">
          {(user?.name || "?")[0]?.toUpperCase()}
        </div>
        <div>
          <h1 className="font-heading font-black text-3xl tracking-tight">{user?.name}</h1>
          <p className="text-muted-foreground font-medium">{user?.email}</p>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <div className="nb-card p-5 bg-accent text-center">
          <Zap className="h-7 w-7 mx-auto mb-1" strokeWidth={2.5} />
          <div className="font-heading font-black text-3xl">{p.xp}</div>
          <div className="font-bold text-sm">XP</div>
        </div>
        <div className="nb-card p-5 bg-warning text-center">
          <Flame className="h-7 w-7 mx-auto mb-1" strokeWidth={2.5} />
          <div className="font-heading font-black text-3xl">{p.streak}</div>
          <div className="font-bold text-sm">Streak</div>
        </div>
        <div className="nb-card p-5 text-center">
          <BookOpen className="h-7 w-7 mx-auto mb-1 text-secondary" strokeWidth={2.5} />
          <div className="font-heading font-black text-3xl">{p.completed_count}</div>
          <div className="font-bold text-sm">Lektionen</div>
        </div>
      </div>

      <div>
        <h2 className="font-heading font-black text-2xl mb-4 flex items-center gap-2">
          <Award className="h-6 w-6 text-primary" strokeWidth={2.5} /> Abzeichen
        </h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
          {BADGES.map((b) => {
            const earned = progress ? b.need(p) : false;
            const Icon = b.icon;
            return (
              <div
                key={b.id}
                data-testid={`badge-${b.id}`}
                className={`nb-card p-5 flex flex-col items-center text-center gap-2 ${earned ? "bg-warning" : "bg-white opacity-60"}`}
              >
                <div className={`h-14 w-14 rounded-full border-2 border-black flex items-center justify-center nb-shadow-sm ${earned ? "bg-white" : "bg-muted"}`}>
                  <Icon className="h-7 w-7" strokeWidth={2.5} />
                </div>
                <div className="font-bold">{b.label}</div>
                <div className="text-xs font-medium text-muted-foreground">{b.desc}</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Notification settings */}
      <div className="nb-card p-6 bg-white border-2 border-black">
        <h2 className="font-heading font-black text-2xl mb-4 flex items-center gap-2">
          <Mail className="h-6 w-6 text-primary" strokeWidth={2.5} /> Benachrichtigungen
        </h2>
        <label className="flex items-center justify-between gap-4 cursor-pointer">
          <div>
            <div className="font-bold">E-Mail bei neuem Abzeichen</div>
            <div className="text-sm text-muted-foreground font-medium">
              Erhalte eine E-Mail, sobald du ein neues Abzeichen freischaltest.
            </div>
          </div>
          <input
            type="checkbox"
            checked={user?.notify_badges_email ?? true}
            onChange={(e) => handleToggleBadgeEmails(e.target.checked)}
            disabled={savingNotifications}
            data-testid="notify-badges-email-toggle"
            className="h-6 w-6 shrink-0 accent-primary border-2 border-black rounded cursor-pointer disabled:opacity-50"
          />
        </label>
      </div>

      {/* Edit Credentials Section */}
      <div className="nb-card p-6 bg-white border-2 border-black">
        <h2 className="font-heading font-black text-2xl mb-4 flex items-center gap-2">
          Zugangsdaten aendern
        </h2>
        <form onSubmit={handleUpdate} className="space-y-4">
          <div>
            <label className="block text-sm font-bold mb-1">Name</label>
            <input
              type="text"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              className="w-full px-3 py-2 border-2 border-black rounded-lg font-bold text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              required
              data-testid="edit-name"
            />
          </div>
          <div>
            <label className="block text-sm font-bold mb-1">E-Mail-Adresse</label>
            <input
              type="email"
              value={editEmail}
              onChange={(e) => setEditEmail(e.target.value)}
              className="w-full px-3 py-2 border-2 border-black rounded-lg font-bold text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              required
              data-testid="edit-email"
            />
          </div>
          <div>
            <label className="block text-sm font-bold mb-1">Neues Passwort (leer lassen fuer keine Aenderung)</label>
            <input
              type="password"
              value={editPassword}
              onChange={(e) => setEditPassword(e.target.value)}
              className="w-full px-3 py-2 border-2 border-black rounded-lg font-bold text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="Mindestens 10 Zeichen"
              data-testid="edit-password"
            />
          </div>
          {editPassword && (
            <div>
              <label className="block text-sm font-bold mb-1">Neues Passwort bestaetigen</label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full px-3 py-2 border-2 border-black rounded-lg font-bold text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                required
                data-testid="edit-confirm-password"
              />
            </div>
          )}
          <button
            type="submit"
            disabled={loading}
            className="px-5 py-2.5 bg-primary text-white border-2 border-black rounded-lg font-bold text-sm nb-shadow-sm nb-press hover:bg-black hover:text-white transition-all disabled:opacity-50"
            data-testid="save-credentials-button"
          >
            {loading ? "Speichern..." : "Aenderungen speichern"}
          </button>
        </form>
      </div>

      {/* Danger zone */}
      <div className="nb-card p-6 border-2 border-error">
        <h2 className="font-heading font-black text-2xl mb-4 flex items-center gap-2 text-error">
          <AlertTriangle className="h-6 w-6" strokeWidth={2.5} /> Gefahrenzone
        </h2>

        <div className="space-y-3 pb-5 mb-5 border-b-2 border-black">
          <div>
            <h3 className="font-bold">Meine Daten zurücksetzen</h3>
            <p className="text-sm text-muted-foreground font-medium">
              Setzt XP, Streak, Lektionen, Quizze, Challenges, Tages-Challenge-Serie und Flashcards zurück. Dein Konto bleibt erhalten.
            </p>
          </div>
          <button
            type="button"
            onClick={handleResetData}
            disabled={resettingData}
            data-testid="reset-my-data-button"
            className="flex items-center gap-2 px-4 py-2 bg-warning border-2 border-black rounded-lg font-bold text-sm nb-shadow-sm nb-press disabled:opacity-50"
          >
            {resettingData ? <Loader2 className="h-4 w-4 animate-spin" strokeWidth={2.5} /> : <RotateCcw className="h-4 w-4" strokeWidth={2.5} />}
            Alle meine Daten zurücksetzen
          </button>
        </div>

        <div className="space-y-3">
          <div>
            <h3 className="font-bold">Konto endgültig löschen</h3>
            <p className="text-sm text-muted-foreground font-medium">
              Löscht dein Konto und alle damit verbundenen Daten unwiderruflich. Diese Aktion kann nicht rückgängig gemacht werden.
            </p>
          </div>
          {user?.role === "admin" ? (
            <p className="text-sm font-bold bg-muted border-2 border-black rounded-lg px-3 py-2 inline-block">
              Administrator-Konten können hier nicht selbst gelöscht werden.
            </p>
          ) : (
            <form onSubmit={handleDeleteAccount} className="flex flex-col sm:flex-row items-stretch sm:items-end gap-2">
              <div className="flex-1">
                <label className="block text-sm font-bold mb-1">Passwort zur Bestätigung</label>
                <input
                  type="password"
                  value={deletePassword}
                  onChange={(e) => setDeletePassword(e.target.value)}
                  className="w-full px-3 py-2 border-2 border-black rounded-lg font-bold text-sm focus:outline-none focus:ring-2 focus:ring-error"
                  placeholder="Dein aktuelles Passwort"
                  data-testid="delete-account-password"
                  required
                />
              </div>
              <button
                type="submit"
                disabled={deletingAccount}
                data-testid="delete-account-button"
                className="flex items-center gap-2 px-4 py-2.5 bg-error text-white border-2 border-black rounded-lg font-bold text-sm nb-shadow-sm nb-press disabled:opacity-50 shrink-0"
              >
                <Trash2 className="h-4 w-4" strokeWidth={2.5} />
                Konto löschen
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
